import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, IconButton, Button,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar,
  Tooltip, Divider, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Autocomplete, FormControlLabel, Switch, Tabs, Tab,
  Menu, ListItemIcon, ListItemText, Stack, Avatar, useTheme, useMediaQuery,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon,
  Close as CloseIcon, Refresh as RefreshIcon, Print as PrintIcon,
  Description as BudgetIcon, Person as PersonIcon,
  CheckCircle as ApproveIcon, Cancel as RejectIcon, Send as SendIcon,
  ContentCopy as DuplicateIcon, MoreVert as MoreIcon,
  AttachMoney as MoneyIcon, CalendarToday as CalendarIcon, Share as ShareIcon,
  MedicalServices as MedicalIcon, Visibility as ViewIcon,
  TrendingUp as TrendingUpIcon, Receipt as ReceiptIcon,
  RemoveCircleOutline as RemoveIcon, AddCircleOutline as AddItemIcon,
  AutoAwesome as AiIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { handleCashRegisterRequired } from '../../utils/cashRegisterPrompt';
import SendDocumentDialog from '../../components/SendDocumentDialog';
import BudgetFormDialog from '../../components/BudgetFormDialog';

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
function TabPanel({ children, value, index }: TabPanelProps) {
  return <div hidden={value !== index}>{value === index && <Box sx={{ pt: 2 }}>{children}</Box>}</div>;
}

const statusLabels: Record<string, string> = {
  draft: 'Rascunho', sent: 'Enviado', approved: 'Aprovado', rejected: 'Rejeitado',
  expired: 'Expirado', cancelled: 'Cancelado', in_treatment: 'Em Tratamento', completed: 'Concluído',
};
const statusColors: Record<string, string> = {
  draft: '#6b7280', sent: '#3b82f6', approved: '#10b981', rejected: '#ef4444',
  expired: '#f59e0b', cancelled: '#6b7280', in_treatment: '#8b5cf6', completed: '#14b8a6',
};


const Budgets: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTabletOrDown = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const budgetPatientPrefillRef = React.useRef<string | null>(null);
  
  // Determinar se o usuário é profissional vinculado
  const isProfessionalUser = user?.role === 'professional' && !!user?.professional_id;
  const userProfessionalId = user?.professional_id || '';

  const [budgets, setBudgets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [prefillPatient, setPrefillPatient] = useState<any>(null);
  // tabValue removed (was form dialog tab, now managed inside BudgetFormDialog)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [stats, setStats] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBudget, setSelectedBudget] = useState<any>(null);
  const [viewBudget, setViewBudget] = useState<any>(null);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printData, setPrintData] = useState<any>(null);
  const printRef = useRef<HTMLDivElement>(null);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendDocData, setSendDocData] = useState<any>(null);
  const [budgetProgress, setBudgetProgress] = useState<any>(null);

  // Fetch budgets
  const fetchBudgets = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { limit: 100 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      // Se o usuário é profissional vinculado, filtrar por seu professional_id
      if (isProfessionalUser && userProfessionalId) {
        params.professional_id = userProfessionalId;
      }
      const res = await axios.get(getApiUrl('/clinic/budgets'), { params });
      setBudgets(res.data?.data || []);
    } catch (err: any) {
      console.error('Error fetching budgets:', err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, isProfessionalUser, userProfessionalId]);

  const fetchStats = useCallback(async () => {
    try {
      const params: any = {};
      // Se o usuário é profissional vinculado, filtrar por seu professional_id
      if (isProfessionalUser && userProfessionalId) {
        params.professional_id = userProfessionalId;
      }
      const res = await axios.get(getApiUrl('/clinic/budgets/stats'), { params });
      setStats(res.data?.data);
    } catch (err) {
      console.error('Error fetching budget stats:', err);
    }
  }, [isProfessionalUser, userProfessionalId]);

  useEffect(() => { fetchBudgets(); fetchStats(); }, [fetchBudgets, fetchStats]);

  /** Abrir novo orçamento com paciente pré-selecionado via `?patientId=` na URL. */
  useEffect(() => {
    const pid = searchParams.get('patientId');
    if (!pid || budgetPatientPrefillRef.current === pid) return;
    budgetPatientPrefillRef.current = pid;

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(getApiUrl(`/clinic/healthcare-panel/patient/${pid}/summary`));
        const p = res.data?.data?.patient;
        if (cancelled) return;
        if (!p?.id) {
          setSnackbar({ open: true, message: 'Paciente não encontrado.', severity: 'error' });
          budgetPatientPrefillRef.current = null;
        } else {
          setEditingId(null);
          setPrefillPatient(p);
          setDialogOpen(true);
        }
        const next = new URLSearchParams(searchParams);
        next.delete('patientId');
        setSearchParams(next, { replace: true });
      } catch {
        if (!cancelled) {
          budgetPatientPrefillRef.current = null;
          setSnackbar({ open: true, message: 'Não foi possível carregar o paciente.', severity: 'error' });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams, setSearchParams]);


  // Edit budget – formulário agora é gerenciado pelo BudgetFormDialog
  const handleEdit = (budget: any) => {
    setPrefillPatient(null);
    setEditingId(budget.id);
    setDialogOpen(true);
  };

  // View budget
  const handleView = async (budget: any) => {
    try {
      const [budgetRes, progressRes] = await Promise.all([
        axios.get(getApiUrl(`/clinic/budgets/${budget.id}`)),
        axios.get(getApiUrl(`/clinic/budgets/${budget.id}/progress`)).catch(() => ({ data: { data: null } })),
      ]);
      setViewBudget(budgetRes.data?.data);
      setBudgetProgress(progressRes.data?.data || null);
      setViewDialogOpen(true);
    } catch (err) {
      console.error('Error fetching budget:', err);
    }
  };

  // Status actions
  const handleStatusChange = async (id: string, status: string, notes?: string) => {
    try {
      const res = await axios.patch(getApiUrl(`/clinic/budgets/${id}/status`), { status, notes });
      const auto = res.data?.data?._auto;
      let msg = `Status atualizado para: ${statusLabels[status]}`;
      if (auto?.treatment_plan_created) msg += ' | Plano de tratamento criado automaticamente';
      if (auto?.receivables_created > 0) msg += ` | ${auto.receivables_created} conta(s) a receber gerada(s)`;
      setSnackbar({ open: true, message: msg, severity: 'success' });
      fetchBudgets();
      fetchStats();
      setAnchorEl(null);
    } catch (err: any) {
      if (handleCashRegisterRequired(err, (p) => navigate(p))) return;
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro ao atualizar status', severity: 'error' });
    }
  };

  // Duplicate
  const handleDuplicate = async (id: string) => {
    try {
      await axios.post(getApiUrl(`/clinic/budgets/${id}/duplicate`));
      setSnackbar({ open: true, message: 'Orçamento duplicado com sucesso!', severity: 'success' });
      fetchBudgets();
      fetchStats();
      setAnchorEl(null);
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro ao duplicar', severity: 'error' });
    }
  };

  // Delete
  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este orçamento?')) return;
    try {
      await axios.delete(getApiUrl(`/clinic/budgets/${id}`));
      setSnackbar({ open: true, message: 'Orçamento excluído com sucesso!', severity: 'success' });
      fetchBudgets();
      fetchStats();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro ao excluir', severity: 'error' });
    }
  };

  // Print
  const handlePrint = async (budget: any) => {
    try {
      const res = await axios.get(getApiUrl(`/clinic/budgets/${budget.id}/print`));
      setPrintData(res.data?.data);
      setPrintDialogOpen(true);
    } catch (err) {
      console.error('Error fetching print data:', err);
    }
  };

  const executePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Orçamento ${printData?.budget?.budget_number || ''}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #1f2937; padding: 20px; }
            .print-container { max-width: 210mm; margin: 0 auto; }
            .doc-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; border-bottom: 3px solid #14B8A6; padding-bottom: 14px; margin-bottom: 20px; }
            .doc-head-logo { flex-shrink: 0; }
            .doc-head-logo img { display: block; max-width: 150px; max-height: 56px; width: auto; height: auto; object-fit: contain; object-position: left top; }
            .doc-head-center { flex: 1; min-width: 0; text-align: center; align-self: center; }
            .doc-head-meta { flex-shrink: 0; max-width: 42%; font-size: 10px; color: #6b7280; text-align: right; line-height: 1.45; }
            .doc-head .clinic-name { font-size: 18px; font-weight: 700; color: #0d9488; margin: 0; }
            .clinic-subtitle { font-size: 13px; color: #6b7280; margin-top: 2px; }
            .budget-title { text-align: center; font-size: 18px; font-weight: 700; color: #0d9488; margin: 16px 0; padding: 8px; background: #f0fdf4; border-radius: 8px; }
            .budget-number { font-size: 12px; color: #6b7280; text-align: center; margin-bottom: 16px; }
            .section { margin-bottom: 16px; }
            .section-title { font-size: 13px; font-weight: 700; color: #0d9488; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; padding-bottom: 4px; border-bottom: 1px solid #e5e7eb; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 16px; }
            .info-item { font-size: 12px; }
            .info-label { color: #6b7280; font-weight: 600; }
            .info-value { color: #1f2937; }
            table { width: 100%; border-collapse: collapse; margin: 8px 0; }
            th { background: #0d9488; color: white; font-size: 11px; font-weight: 600; padding: 8px 10px; text-align: left; }
            td { font-size: 11px; padding: 7px 10px; border-bottom: 1px solid #e5e7eb; }
            tr:nth-child(even) { background: #f9fafb; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .totals { margin-top: 12px; display: flex; justify-content: flex-end; }
            .totals-table { width: 280px; }
            .totals-table td { padding: 4px 8px; font-size: 12px; }
            .total-row { font-size: 16px !important; font-weight: 700; color: #0d9488; border-top: 2px solid #0d9488; }
            .payment-box { background: #f0fdf4; border: 1px solid #d1fae5; border-radius: 8px; padding: 12px; margin: 12px 0; }
            .payment-box p { font-size: 12px; margin-bottom: 4px; }
            .terms { font-size: 10px; color: #6b7280; margin-top: 16px; padding: 12px; background: #f9fafb; border-radius: 6px; border: 1px solid #e5e7eb; }
            .signatures { display: flex; justify-content: space-between; margin-top: 40px; padding-top: 20px; }
            .signature-line { text-align: center; width: 45%; }
            .signature-line hr { border: none; border-top: 1px solid #374151; margin-bottom: 6px; }
            .signature-line p { font-size: 11px; color: #6b7280; }
            .prof-sig-print { text-align: center; margin-bottom: 8px; }
            .prof-sig-print img { max-height: 90px; max-width: 360px; object-fit: contain; display: inline-block; vertical-align: bottom; }
            .validity { text-align: center; font-size: 11px; color: #ef4444; font-weight: 600; margin: 12px 0; }
            @media print {
              body { padding: 0; }
              @page { margin: 15mm; size: A4; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 500);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const formatDate = (date: string) => {
    if (!date) return '-';
    return new Date(date + 'T00:00:00').toLocaleDateString('pt-BR');
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, backgroundColor: 'transparent', minHeight: '100vh', pb: { xs: 10, sm: 3 } }}>
      {/* Header */}
      <Box sx={{
        mb: { xs: 2, md: 3 },
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2,
      }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2, mb: 0.5 }}>
            Orçamentos
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Gerencie orçamentos para todas as especialidades médicas e odontológicas
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, width: { xs: '100%', sm: 'auto' }, flexWrap: 'wrap' }}>
          <Button variant="outlined" fullWidth={isMobile} startIcon={<RefreshIcon />} onClick={() => { fetchBudgets(); fetchStats(); }}
            sx={{ borderColor: '#e5e7eb', color: '#64748b', textTransform: 'none' }}>
            Atualizar
          </Button>
          <Button variant="outlined" fullWidth={isMobile} startIcon={<AiIcon />}
            onClick={() => { setPrefillPatient(null); setEditingId(null); setDialogOpen(true); }}
            sx={{ borderColor: '#8b5cf6', color: '#8b5cf6', '&:hover': { borderColor: '#7c3aed', bgcolor: 'rgba(139,92,246,0.04)' }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            Criar com IA
          </Button>
          <Button variant="contained" fullWidth={isMobile} startIcon={<AddIcon />}
            onClick={() => { setPrefillPatient(null); setEditingId(null); setDialogOpen(true); }}
            sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            Novo Orçamento
          </Button>
        </Box>
      </Box>

      {/* KPI Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total de Orçamentos', value: stats.total, icon: <BudgetIcon />, color: '#14b8a6' },
            { label: 'Este Mês', value: stats.this_month, icon: <CalendarIcon />, color: '#3b82f6' },
            { label: 'Aprovados', value: stats.by_status?.approved || 0, icon: <ApproveIcon />, color: '#10b981' },
            { label: 'Taxa de Conversão', value: `${stats.conversion_rate || 0}%`, icon: <TrendingUpIcon />, color: '#f59e0b' },
            { label: 'Valor Aprovado', value: formatCurrency(stats.approved_value), icon: <MoneyIcon />, color: '#14b8a6' },
            { label: 'Valor Pendente', value: formatCurrency(stats.pending_value), icon: <ReceiptIcon />, color: '#ef4444' },
          ].map((kpi, i) => (
            <Grid item xs={6} md={2} key={i}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box sx={{ p: 0.8, borderRadius: 2, bgcolor: `${kpi.color}15` }}>
                      {React.cloneElement(kpi.icon, { sx: { fontSize: 20, color: kpi.color } })}
                    </Box>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937', fontSize: '1.2rem' }}>{kpi.value}</Typography>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.7rem' }}>{kpi.label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
        <CardContent sx={{ py: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" placeholder="Buscar por paciente, número, CPF, telefone..."
                value={search} onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#9ca3af' }} /></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                  <MenuItem value="">Todos</MenuItem>
                  {Object.entries(statusLabels).map(([key, label]) => (
                    <MenuItem key={key} value={key}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" sx={{ color: '#6b7280', textAlign: 'right' }}>
                {budgets.length} orçamento(s) encontrado(s)
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Lista: cards (mobile/tablet) | tabela (desktop) */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: '#14b8a6' }} /></Box>
      ) : budgets.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 8, borderRadius: 3 }}>
          <BudgetIcon sx={{ fontSize: 64, color: '#d1d5db', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#6b7280' }}>Nenhum orçamento encontrado</Typography>
          <Typography variant="body2" sx={{ color: '#9ca3af', mb: 2 }}>Crie seu primeiro orçamento clicando no botão acima</Typography>
        </Card>
      ) : isTabletOrDown ? (
        <Stack spacing={1.5} sx={{ width: '100%' }}>
          {budgets.map((budget) => (
            <Card
              key={budget.id}
              elevation={0}
              onClick={() => handleView(budget)}
              sx={{
                borderRadius: 2,
                border: '1px solid #e5e7eb',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                cursor: 'pointer',
                '&:active': { transform: 'scale(0.99)' },
              }}
            >
              <CardContent sx={{ py: 1.75, px: 2, '&:last-child': { pb: 1.75 } }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Avatar sx={{ width: 44, height: 44, bgcolor: '#14b8a620', color: '#0d9488', fontSize: 16, flexShrink: 0 }}>
                    {(budget.patient_name || '?').charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488' }}>{budget.budget_number}</Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>
                      {budget.patient_name}
                    </Typography>
                    {budget.patient_phone && (
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>{budget.patient_phone}</Typography>
                    )}
                    <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mt: 0.5 }}>
                      {formatDate(budget.issue_date)} · vál. {formatDate(budget.validity_date)}
                    </Typography>
                    {budget.professional_name && (
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.25 }}>{budget.professional_name}</Typography>
                    )}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', mt: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: '#0d9488' }}>{formatCurrency(budget.total)}</Typography>
                      <Chip label={statusLabels[budget.status] || budget.status} size="small"
                        sx={{ bgcolor: `${statusColors[budget.status]}20`, color: statusColors[budget.status], fontWeight: 600, fontSize: '0.7rem' }} />
                      <Chip label={`${budget.items?.length || 0} itens`} size="small" variant="outlined" sx={{ borderColor: '#d1fae5', color: '#0d9488', fontWeight: 600 }} />
                    </Box>
                  </Box>
                </Box>
                <Divider sx={{ my: 1.25 }} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Imprimir">
                    <IconButton size="medium" onClick={() => handlePrint(budget)} sx={{ color: '#14b8a6' }}><PrintIcon /></IconButton>
                  </Tooltip>
                  <IconButton size="medium" onClick={() => { setSendDocData({ id: budget.id, patientId: budget.patient_id, email: budget.patient?.email || budget.patient_email, phone: budget.patient?.phone || budget.patient_phone, whatsapp: budget.patient?.whatsapp, name: budget.patient?.full_name || budget.patient_name }); setSendDialogOpen(true); }} sx={{ color: '#3b82f6' }}><ShareIcon /></IconButton>
                  <Tooltip title="Editar">
                    <IconButton size="medium" onClick={() => handleEdit(budget)} sx={{ color: '#3b82f6' }}><EditIcon /></IconButton>
                  </Tooltip>
                  <Tooltip title="Mais ações">
                    <IconButton size="medium" onClick={(e) => { setSelectedBudget(budget); setAnchorEl(e.currentTarget); }}><MoreIcon /></IconButton>
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f0fdf4' }}>
                <TableCell sx={{ fontWeight: 700, color: '#0d9488' }}>Número</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#0d9488' }}>Paciente</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#0d9488' }}>Profissional</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#0d9488' }}>Data</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#0d9488' }}>Validade</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#0d9488' }} align="right">Total</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#0d9488' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#0d9488' }} align="center">Itens</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#0d9488' }} align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {budgets.map((budget) => (
                <TableRow key={budget.id} hover sx={{ '&:hover': { bgcolor: '#f0fdf4' }, cursor: 'pointer' }}
                  onClick={() => handleView(budget)}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0d9488' }}>{budget.budget_number}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{budget.patient_name}</Typography>
                    {budget.patient_phone && <Typography variant="caption" sx={{ color: '#6b7280' }}>{budget.patient_phone}</Typography>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{budget.professional_name || '-'}</Typography>
                    {budget.professional_registration && <Typography variant="caption" sx={{ color: '#6b7280' }}>{budget.professional_registration}</Typography>}
                  </TableCell>
                  <TableCell><Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{formatDate(budget.issue_date)}</Typography></TableCell>
                  <TableCell><Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{formatDate(budget.validity_date)}</Typography></TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0d9488' }}>{formatCurrency(budget.total)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={statusLabels[budget.status] || budget.status} size="small"
                      sx={{ bgcolor: `${statusColors[budget.status]}20`, color: statusColors[budget.status], fontWeight: 600, fontSize: '0.7rem' }} />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={budget.items?.length || 0} size="small" sx={{ bgcolor: '#f3f4f6' }} />
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <Tooltip title="Imprimir">
                        <IconButton size="small" onClick={() => handlePrint(budget)}>
                          <PrintIcon sx={{ fontSize: 18, color: '#14b8a6' }} />
                        </IconButton>
                      </Tooltip>
                      <IconButton size="small" onClick={() => { setSendDocData({ id: budget.id, patientId: budget.patient_id, email: budget.patient?.email || budget.patient_email, phone: budget.patient?.phone || budget.patient_phone, whatsapp: budget.patient?.whatsapp, name: budget.patient?.full_name || budget.patient_name }); setSendDialogOpen(true); }} sx={{ color: '#3b82f6' }}><ShareIcon fontSize="small" /></IconButton>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleEdit(budget)}>
                          <EditIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Mais ações">
                        <IconButton size="small" onClick={(e) => { setSelectedBudget(budget); setAnchorEl(e.currentTarget); }}>
                          <MoreIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Context Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {selectedBudget?.status === 'draft' && (
          <MenuItem onClick={() => handleStatusChange(selectedBudget.id, 'sent')}>
            <ListItemIcon><SendIcon sx={{ color: '#3b82f6' }} /></ListItemIcon>
            <ListItemText>Marcar como Enviado</ListItemText>
          </MenuItem>
        )}
        {(selectedBudget?.status === 'draft' || selectedBudget?.status === 'sent') && (
          <MenuItem onClick={() => handleStatusChange(selectedBudget.id, 'approved')}>
            <ListItemIcon><ApproveIcon sx={{ color: '#10b981' }} /></ListItemIcon>
            <ListItemText>Aprovar</ListItemText>
          </MenuItem>
        )}
        {(selectedBudget?.status === 'draft' || selectedBudget?.status === 'sent') && (
          <MenuItem onClick={() => { const reason = window.prompt('Motivo da rejeição:'); if (reason !== null) handleStatusChange(selectedBudget.id, 'rejected', reason); }}>
            <ListItemIcon><RejectIcon sx={{ color: '#ef4444' }} /></ListItemIcon>
            <ListItemText>Rejeitar</ListItemText>
          </MenuItem>
        )}
        {(selectedBudget?.status === 'approved' || selectedBudget?.status === 'in_treatment') && (
          <MenuItem onClick={() => handleStatusChange(selectedBudget.id, 'completed')}>
            <ListItemIcon><ApproveIcon sx={{ color: '#14b8a6' }} /></ListItemIcon>
            <ListItemText>Concluir Tratamento</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => handleDuplicate(selectedBudget?.id)}>
          <ListItemIcon><DuplicateIcon sx={{ color: '#6b7280' }} /></ListItemIcon>
          <ListItemText>Duplicar</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { handleDelete(selectedBudget?.id); setAnchorEl(null); }}>
          <ListItemIcon><DeleteIcon sx={{ color: '#ef4444' }} /></ListItemIcon>
          <ListItemText>Excluir</ListItemText>
        </MenuItem>
      </Menu>

      {/* ====== CREATE/EDIT DIALOG – via BudgetFormDialog ====== */}
      <BudgetFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        initialPatient={prefillPatient}
        editingBudgetId={editingId}
        onSaved={() => { fetchBudgets(); fetchStats(); }}
      />
      {/* ====== VIEW DIALOG ====== */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        PaperProps={{
          sx: isMobile
            ? { m: 0, borderRadius: 0, height: '100%', maxHeight: '100%', display: 'flex', flexDirection: 'column' }
            : { borderRadius: 3 },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb', flexShrink: 0, px: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BudgetIcon sx={{ color: '#14b8a6' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Orçamento {viewBudget?.budget_number}</Typography>
            {viewBudget && <Chip label={statusLabels[viewBudget.status]} size="small" sx={{ bgcolor: `${statusColors[viewBudget.status]}20`, color: statusColors[viewBudget.status], fontWeight: 600 }} />}
          </Box>
          <IconButton onClick={() => setViewDialogOpen(false)} aria-label="Fechar"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 2, sm: 3 }, flex: 1, overflow: 'auto' }}>
          {viewBudget && (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488', mb: 1 }}>Paciente</Typography>
                  <Typography variant="body2"><strong>Nome:</strong> {viewBudget.patient_name}</Typography>
                  {viewBudget.patient_cpf && <Typography variant="body2"><strong>CPF:</strong> {viewBudget.patient_cpf}</Typography>}
                  {viewBudget.patient_phone && <Typography variant="body2"><strong>Telefone:</strong> {viewBudget.patient_phone}</Typography>}
                  {viewBudget.patient_email && <Typography variant="body2"><strong>Email:</strong> {viewBudget.patient_email}</Typography>}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488', mb: 1 }}>Profissional</Typography>
                  <Typography variant="body2"><strong>Nome:</strong> {viewBudget.professional_name || '-'}</Typography>
                  {viewBudget.professional_registration && <Typography variant="body2"><strong>Registro:</strong> {viewBudget.professional_registration}</Typography>}
                  {viewBudget.professional_specialty && <Typography variant="body2"><strong>Especialidade:</strong> {viewBudget.professional_specialty}</Typography>}
                </Grid>
              </Grid>

              {budgetProgress?.has_plan && (
                <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MedicalIcon sx={{ fontSize: 18 }} /> Progresso do Tratamento
                    </Typography>
                    <Chip
                      label={`${budgetProgress.completed_items}/${budgetProgress.total_items} procedimento(s)`}
                      size="small"
                      sx={{ fontWeight: 700, bgcolor: budgetProgress.pending_items === 0 ? '#dcfce7' : '#fef3c7', color: budgetProgress.pending_items === 0 ? '#16a34a' : '#d97706' }}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={budgetProgress.total_items > 0 ? (budgetProgress.completed_items / budgetProgress.total_items) * 100 : 0}
                    sx={{ height: 8, borderRadius: 4, bgcolor: '#dcfce7', '& .MuiLinearProgress-bar': { bgcolor: budgetProgress.pending_items === 0 ? '#16a34a' : '#0d9488', borderRadius: 4 } }}
                  />
                  {budgetProgress.pending_items > 0 && viewBudget?.patient_id && (
                    <Typography variant="caption" sx={{ color: '#64748b', mt: 1, display: 'block' }}>
                      {budgetProgress.pending_items} procedimento(s) pendente(s).{' '}
                      <a href={`/clinic/patients?id=${viewBudget.patient_id}`} target="_blank" rel="noopener noreferrer" style={{ color: '#0d9488', fontWeight: 600 }}>
                        Executar no prontuário do paciente
                      </a>
                    </Typography>
                  )}
                  {budgetProgress.pending_items === 0 && (
                    <Typography variant="caption" sx={{ color: '#16a34a', mt: 1, display: 'block', fontWeight: 600 }}>
                      Todos os procedimentos foram executados.
                    </Typography>
                  )}
                </Box>
              )}

              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488', mb: 1 }}>Itens do Orçamento</Typography>
              <TableContainer sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f0fdf4' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Procedimento</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Código</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Dente</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">Qtd</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Unitário</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Desc.</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Total</TableCell>
                      {budgetProgress?.has_plan && <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(viewBudget.items || []).map((item: any) => {
                      const itemStatus = item.status || 'pending';
                      const itemStatusLabel = itemStatus === 'completed' ? 'Executado' : itemStatus === 'in_progress' ? 'Em andamento' : itemStatus === 'cancelled' ? 'Cancelado' : 'Pendente';
                      const itemStatusColor = itemStatus === 'completed' ? '#16a34a' : itemStatus === 'in_progress' ? '#3b82f6' : itemStatus === 'cancelled' ? '#ef4444' : '#94a3b8';
                      return (
                      <TableRow key={item.id}>
                        <TableCell>{item.procedure_name}</TableCell>
                        <TableCell>{item.procedure_code || '-'}</TableCell>
                        <TableCell>{item.tooth_number ? `${item.tooth_number}${item.tooth_face ? ` (${item.tooth_face})` : ''}` : '-'}</TableCell>
                        <TableCell align="center">{item.quantity}</TableCell>
                        <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                        <TableCell align="right">{item.discount_percentage > 0 ? `${item.discount_percentage}%` : '-'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(item.total)}</TableCell>
                        {budgetProgress?.has_plan && (
                          <TableCell align="center">
                            <Chip label={itemStatusLabel} size="small" sx={{ fontSize: '0.65rem', fontWeight: 600, bgcolor: `${itemStatusColor}15`, color: itemStatusColor, height: 20 }} />
                          </TableCell>
                        )}
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Card sx={{ p: 2, bgcolor: '#f0fdf4', border: '1px solid #d1fae5', borderRadius: 2 }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}><Typography variant="body2">Subtotal:</Typography></Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}><Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(viewBudget.subtotal)}</Typography></Grid>
                  {viewBudget.discount_amount > 0 && <>
                    <Grid item xs={6}><Typography variant="body2">Desconto:</Typography></Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}><Typography variant="body2" sx={{ color: '#ef4444', fontWeight: 600 }}>-{formatCurrency(viewBudget.discount_amount)}</Typography></Grid>
                  </>}
                  <Grid item xs={12}><Divider /></Grid>
                  <Grid item xs={6}><Typography variant="h6" sx={{ fontWeight: 700 }}>TOTAL:</Typography></Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}><Typography variant="h6" sx={{ fontWeight: 700, color: '#0d9488' }}>{formatCurrency(viewBudget.total)}</Typography></Grid>
                  {viewBudget.payment_method && <>
                    <Grid item xs={6}><Typography variant="body2">Pagamento:</Typography></Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}><Typography variant="body2">{viewBudget.payment_method} em {viewBudget.installments}x de {formatCurrency(viewBudget.installment_value)}</Typography></Grid>
                  </>}
                </Grid>
              </Card>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 2 }, borderTop: '1px solid #e5e7eb', gap: 1, flexWrap: 'wrap', flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
          <Button fullWidth={isMobile} onClick={() => setViewDialogOpen(false)} sx={{ textTransform: 'none' }}>Fechar</Button>
          <Button fullWidth={isMobile} onClick={() => { if (viewBudget) handleEdit(viewBudget); setViewDialogOpen(false); }} startIcon={<EditIcon />}
            sx={{ color: '#3b82f6', textTransform: 'none', fontWeight: 600 }}>Editar</Button>
          <Button fullWidth={isMobile} onClick={() => { if (viewBudget) handlePrint(viewBudget); }} startIcon={<PrintIcon />}
            sx={{ color: '#14b8a6', textTransform: 'none', fontWeight: 600 }}>Imprimir</Button>
        </DialogActions>
      </Dialog>

      {/* ====== PRINT DIALOG ====== */}
      <Dialog
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3, m: isMobile ? 0 : 2, maxHeight: isMobile ? '100%' : undefined } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Pré-visualização do Orçamento</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" startIcon={<PrintIcon />} onClick={executePrint}
              sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', fontWeight: 600 }}>
              Imprimir
            </Button>
            <IconButton onClick={() => setPrintDialogOpen(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {printData && (
            <div ref={printRef}>
              <div className="print-container">
                {/* Header */}
                <div className="doc-head">
                  {printData.clinic?.logo_url && (
                    <div className="doc-head-logo"><img src={printData.clinic.logo_url} alt="" /></div>
                  )}
                  <div className="doc-head-center">
                    <div className="clinic-name">{printData.clinic?.clinic_name || 'Clínica'}</div>
                    {printData.clinic?.clinic_subtitle && <div className="clinic-subtitle">{printData.clinic.clinic_subtitle}</div>}
                  </div>
                  <div className="doc-head-meta">
                    {printData.clinic?.cnpj && <div>CNPJ: {printData.clinic.cnpj}</div>}
                    {printData.clinic?.address_street && (
                      <div>{printData.clinic.address_street}{printData.clinic.address_number ? `, ${printData.clinic.address_number}` : ''}{printData.clinic.address_neighborhood ? ` — ${printData.clinic.address_neighborhood}` : ''}</div>
                    )}
                    {(printData.clinic?.address_city || printData.clinic?.address_state) && (
                      <div>{[printData.clinic.address_city, printData.clinic.address_state].filter(Boolean).join(' - ')}</div>
                    )}
                    {(printData.clinic?.phone || printData.clinic?.whatsapp) && (
                      <div>Tel: {printData.clinic.phone || printData.clinic.whatsapp}</div>
                    )}
                    {printData.clinic?.email && <div>{printData.clinic.email}</div>}
                  </div>
                </div>

                {/* Budget title */}
                <div className="budget-title">ORÇAMENTO</div>
                <div className="budget-number">
                  Nº {printData.budget?.budget_number} | Emissão: {formatDate(printData.budget?.issue_date)} | Validade: {formatDate(printData.budget?.validity_date)}
                </div>

                {/* Patient info */}
                <div className="section">
                  <div className="section-title">Dados do Paciente</div>
                  <div className="info-grid">
                    <div className="info-item"><span className="info-label">Nome: </span><span className="info-value">{printData.budget?.patient_name}</span></div>
                    {printData.budget?.patient_cpf && <div className="info-item"><span className="info-label">CPF: </span><span className="info-value">{printData.budget.patient_cpf}</span></div>}
                    {printData.budget?.patient_phone && <div className="info-item"><span className="info-label">Telefone: </span><span className="info-value">{printData.budget.patient_phone}</span></div>}
                    {printData.budget?.patient_email && <div className="info-item"><span className="info-label">Email: </span><span className="info-value">{printData.budget.patient_email}</span></div>}
                    {printData.budget?.patient_birth_date && <div className="info-item"><span className="info-label">Nascimento: </span><span className="info-value">{formatDate(printData.budget.patient_birth_date)}</span></div>}
                    {printData.budget?.patient_address && <div className="info-item"><span className="info-label">Endereço: </span><span className="info-value">{printData.budget.patient_address}</span></div>}
                  </div>
                </div>

                {/* Professional info */}
                {printData.budget?.show_professional_info && printData.budget?.professional_name && (
                  <div className="section">
                    <div className="section-title">Profissional Responsável</div>
                    <div className="info-grid">
                      <div className="info-item"><span className="info-label">Nome: </span><span className="info-value">{printData.budget.professional_name}</span></div>
                      {printData.budget?.professional_registration && <div className="info-item"><span className="info-label">Registro: </span><span className="info-value">{printData.budget.professional_registration}</span></div>}
                      {printData.budget?.professional_specialty && <div className="info-item"><span className="info-label">Especialidade: </span><span className="info-value">{printData.budget.professional_specialty}</span></div>}
                    </div>
                  </div>
                )}

                {/* Clinical indication */}
                {(printData.budget?.clinical_indication || printData.budget?.diagnosis) && (
                  <div className="section">
                    <div className="section-title">Informações Clínicas</div>
                    {printData.budget?.clinical_indication && <div className="info-item" style={{ marginBottom: '4px' }}><span className="info-label">Indicação: </span><span className="info-value">{printData.budget.clinical_indication}</span></div>}
                    {printData.budget?.diagnosis && <div className="info-item"><span className="info-label">Diagnóstico: </span><span className="info-value">{printData.budget.diagnosis}{printData.budget?.diagnosis_code ? ` (CID: ${printData.budget.diagnosis_code})` : ''}</span></div>}
                  </div>
                )}

                {/* Items table */}
                <div className="section">
                  <div className="section-title">Procedimentos</div>
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: '5%' }}>#</th>
                        {printData.budget?.show_procedure_codes && <th style={{ width: '10%' }}>Código</th>}
                        <th>Procedimento</th>
                        {printData.budget?.show_tooth_info && <th style={{ width: '8%' }}>Dente</th>}
                        {printData.budget?.show_tooth_info && <th style={{ width: '8%' }}>Face</th>}
                        <th style={{ width: '5%' }} className="text-center">Qtd</th>
                        {printData.budget?.show_unit_prices && <th style={{ width: '12%' }} className="text-right">Unitário</th>}
                        {printData.budget?.show_unit_prices && <th style={{ width: '8%' }} className="text-right">Desc.</th>}
                        <th style={{ width: '12%' }} className="text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(printData.budget?.items || []).map((item: any, idx: number) => (
                        <tr key={item.id || idx}>
                          <td>{idx + 1}</td>
                          {printData.budget?.show_procedure_codes && <td>{item.procedure_code || '-'}</td>}
                          <td>
                            <strong>{item.procedure_name}</strong>
                            {item.notes && <div style={{ fontSize: '10px', color: '#6b7280' }}>{item.notes}</div>}
                          </td>
                          {printData.budget?.show_tooth_info && <td>{item.tooth_number || '-'}</td>}
                          {printData.budget?.show_tooth_info && <td>{item.tooth_face || '-'}</td>}
                          <td className="text-center">{item.quantity}</td>
                          {printData.budget?.show_unit_prices && <td className="text-right">{formatCurrency(item.unit_price)}</td>}
                          {printData.budget?.show_unit_prices && <td className="text-right">{item.discount_percentage > 0 ? `${item.discount_percentage}%` : '-'}</td>}
                          <td className="text-right"><strong>{formatCurrency(item.total)}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Totals */}
                <div className="totals">
                  <table className="totals-table">
                    <tbody>
                      <tr>
                        <td>Subtotal:</td>
                        <td className="text-right">{formatCurrency(printData.budget?.subtotal)}</td>
                      </tr>
                      {printData.budget?.discount_amount > 0 && (
                        <tr>
                          <td>Desconto{printData.budget?.discount_type === 'percentage' ? ` (${printData.budget?.discount_value}%)` : ''}:</td>
                          <td className="text-right" style={{ color: '#ef4444' }}>-{formatCurrency(printData.budget?.discount_amount)}</td>
                        </tr>
                      )}
                      {printData.budget?.additional_charges > 0 && (
                        <tr>
                          <td>Acréscimos:</td>
                          <td className="text-right">+{formatCurrency(printData.budget?.additional_charges)}</td>
                        </tr>
                      )}
                      <tr className="total-row">
                        <td>TOTAL:</td>
                        <td className="text-right">{formatCurrency(printData.budget?.total)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Payment conditions */}
                {printData.budget?.show_payment_conditions && (printData.budget?.payment_method || printData.budget?.payment_conditions) && (
                  <div className="payment-box">
                    <div className="section-title" style={{ borderBottom: 'none' }}>Condições de Pagamento</div>
                    {printData.budget?.payment_method && <p><strong>Forma:</strong> {printData.budget.payment_method}</p>}
                    {printData.budget?.installments > 1 && (
                      <p><strong>Parcelamento:</strong> {printData.budget.entry_value > 0 ? `Entrada de ${formatCurrency(printData.budget.entry_value)} + ` : ''}{printData.budget.installments}x de {formatCurrency(printData.budget.installment_value)}</p>
                    )}
                    {printData.budget?.payment_conditions && <p>{printData.budget.payment_conditions}</p>}
                  </div>
                )}

                {/* Notes */}
                {printData.budget?.notes && (
                  <div className="section">
                    <div className="section-title">Observações</div>
                    <p style={{ fontSize: '11px', lineHeight: '1.6' }}>{printData.budget.notes}</p>
                  </div>
                )}

                {/* Validity */}
                {printData.budget?.validity_date && (
                  <div className="validity">
                    Orçamento válido até {formatDate(printData.budget.validity_date)}
                  </div>
                )}

                {/* Terms */}
                {printData.budget?.show_terms && printData.clinic?.budget_terms_text && (
                  <div className="terms">
                    <strong>Termos e Condições:</strong><br />
                    {printData.clinic.budget_terms_text}
                  </div>
                )}

                {/* Warranty */}
                {printData.budget?.show_warranty && printData.clinic?.budget_warranty_text && (
                  <div className="terms" style={{ marginTop: '8px' }}>
                    <strong>Garantia:</strong><br />
                    {printData.clinic.budget_warranty_text}
                  </div>
                )}

                {/* Footer */}
                {printData.clinic?.budget_footer_text && (
                  <div style={{ fontSize: '10px', color: '#9ca3af', textAlign: 'center', marginTop: '12px' }}>
                    {printData.clinic.budget_footer_text}
                  </div>
                )}

                {/* Signatures */}
                <div className="signatures">
                  <div className="signature-line">
                    {(() => {
                      const u = String(printData.budget?.professional?.signature_image_url ?? '').trim();
                      return u ? (
                        <div className="prof-sig-print">
                          <img src={u} alt="" />
                        </div>
                      ) : null;
                    })()}
                    <hr />
                    <p>{printData.budget?.professional_name || printData.clinic?.responsible_name || 'Profissional Responsável'}</p>
                    {(printData.budget?.professional_registration || printData.clinic?.responsible_registration) && (
                      <p style={{ fontSize: '10px' }}>{printData.budget?.professional_registration || `${printData.clinic?.responsible_registration_type || 'CRM'} ${printData.clinic?.responsible_registration}`}</p>
                    )}
                  </div>
                  <div className="signature-line">
                    <hr />
                    <p>{printData.budget?.patient_name || 'Paciente'}</p>
                    {printData.budget?.patient_cpf && <p style={{ fontSize: '10px' }}>CPF: {printData.budget.patient_cpf}</p>}
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>

      <SendDocumentDialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        documentType="budget"
        documentId={sendDocData?.id || ''}
        patientId={sendDocData?.patientId}
        patientEmail={sendDocData?.email}
        patientPhone={sendDocData?.phone}
        patientWhatsapp={sendDocData?.whatsapp}
        patientName={sendDocData?.name}
        documentLabel="Orçamento"
      />
    </Box>
  );
};

export default Budgets;
